import { useEffect, useState } from 'react';
import type { HotspotTrack, HotspotsDocument } from '../types';
import { placeHotspot, videoContentRect } from './hotspotMath';

interface HotspotOverlayProps {
  doc: HotspotsDocument;
  /** The high-res scrub video element (sizing + intrinsic dimensions). */
  videoEl: HTMLVideoElement | null;
  /** Current scrub time in seconds. */
  currentTime: number;
  /** Master show/hide toggle. */
  enabled: boolean;
  onOpen: (hotspot: HotspotTrack) => void;
}

/**
 * Clickable hotspot layer drawn over the 360 video.
 *
 * On every scrub the position of each hotspot is interpolated from its
 * radial track and auto-hidden outside its visible arc (with a short
 * fade near the edges). Positions are normalized to the rendered video
 * *content* box, so letterboxing from `object-contain` is accounted for.
 *
 * The layer itself is `pointer-events-none` so drags pass straight
 * through to the viewer; only the markers capture clicks (and stop the
 * pointerdown from starting a drag).
 */
export function HotspotOverlay({ doc, videoEl, currentTime, enabled, onOpen }: HotspotOverlayProps) {
  // Re-measure the video box on resize so markers track the content rect.
  const [, setResizeTick] = useState(0);
  useEffect(() => {
    if (!videoEl) return;
    const ro = new ResizeObserver(() => setResizeTick((n) => n + 1));
    ro.observe(videoEl);
    return () => ro.disconnect();
  }, [videoEl]);

  if (!enabled || !videoEl) return null;
  const rect = videoContentRect(videoEl);
  if (!rect) return null;

  return (
    <div className="absolute inset-0 z-20 pointer-events-none">
      {doc.hotspots.map((h) => {
        const placement = placeHotspot(h, currentTime, doc.video);
        if (!placement) return null;
        const left = rect.offsetX + placement.x * rect.contentW;
        const top = rect.offsetY + placement.y * rect.contentH;
        const isDamage = h.kind === 'damage';
        const iconSrc = isDamage ? '/hotspot-damage.png' : '/hotspot-feature.png';
        const pingClass = isDamage ? 'bg-red-400/70' : 'bg-indigo-400/70';
        return (
          <button
            key={h.id}
            type="button"
            // Stop the drag handler on the viewer container from firing.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onOpen(h);
            }}
            style={{ left, top, opacity: placement.opacity * 0.7 }}
            className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-auto group"
            title={h.label}
            aria-label={h.label}
          >
            <span className="relative block w-8 h-8 rounded-full bg-white shadow-md overflow-hidden transition-transform group-hover:scale-125">
              <img
                src={iconSrc}
                alt=""
                draggable={false}
                className="w-full h-full object-contain p-1 select-none pointer-events-none"
              />
            </span>
            <span className={`absolute inset-0 rounded-full ${pingClass} animate-ping -z-10`} />
          </button>
        );
      })}
    </div>
  );
}
